import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthProvider";
import { enqueueCodeJob, getCodeJob } from "../api/codeApi";
import type { CodeJob } from "../api/codeApi";
import { BLOCK_MESSAGES } from "../messages";

const POLL_INTERVAL_MS = 1000; // Delay between two status checks
const MAX_POLL_ATTEMPTS = 60; // Give up after about a minute

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Runs code on the server: queue the job, then poll until it finishes
export const useCodeRunner = () => {
    const { user } = useAuth();

    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<CodeJob | null>(null);
    const [error, setError] = useState<string | null>(null);

    const abortRef = useRef<AbortController | null>(null);

    // Leaving the page stops the polling
    useEffect(() => () => abortRef.current?.abort(), []);

    const run = useCallback(
        async (language: string, code: string) => {
            if (!user) {
                setError(BLOCK_MESSAGES.loginRequired);
                return;
            }

            // A new run replaces the one still going
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            setRunning(true);
            setResult(null);
            setError(null);

            try {
                const jobId = await enqueueCodeJob(
                    user.id,
                    { code, language, stdin: "" },
                    controller.signal
                );

                for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
                    const job = await getCodeJob(user.id, jobId, controller.signal);

                    if (job.status === "completed" || job.status === "failed") {
                        setResult(job);
                        return;
                    }

                    await sleep(POLL_INTERVAL_MS);
                    if (controller.signal.aborted) return;
                }

                setError(BLOCK_MESSAGES.runTimeout);
            } catch (err) {
                if (axios.isCancel(err)) return; // Stopped on purpose

                // Prefer the server's message (e.g. "Job not found") when there is one
                setError(
                    axios.isAxiosError<{ message?: string }>(err)
                        ? err.response?.data?.message || err.message
                        : err instanceof Error
                            ? err.message
                            : BLOCK_MESSAGES.runFailed
                );
            } finally {
                if (!controller.signal.aborted) setRunning(false);
            }
        },
        [user]
    );

    return { run, running, result, error };
};